import { useEffect, useRef } from 'react';

import { useMap } from '@/hooks/useMap';
import type { Waypoint } from '@/types/waypoint';
import { WaypointType } from '@/types/waypoint';
import type { FoodSourceMetadata } from '@/types/waypoint';

// ─── Layer / source IDs ───────────────────────────────────────────────────────

export const FOOD_PLOT_SOURCE = 'food-plot-boundaries';
export const FOOD_PLOT_FILL = 'food-plot-fill';
export const FOOD_PLOT_LINE = 'food-plot-line';

// ─── Pure helper — exported for tests ────────────────────────────────────────

/**
 * Builds a GeoJSON FeatureCollection from all food plot locations that carry a
 * `boundaryGeoJSON` polygon. Pure function — no side effects.
 */
// eslint-disable-next-line react-refresh/only-export-components -- intentional: pure function exported alongside component for unit tests
export function buildFoodPlotFeatureCollection(
  locations: Waypoint[],
): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];

  for (const loc of locations) {
    if (loc.type !== WaypointType.FoodSource) continue;
    const meta = loc.metadata as FoodSourceMetadata | undefined;
    if (!meta?.boundaryGeoJSON) continue;

    features.push({
      type: 'Feature',
      geometry: meta.boundaryGeoJSON as GeoJSON.Geometry,
      properties: { id: loc.id, name: loc.name },
    });
  }

  return { type: 'FeatureCollection', features };
}

// ─── Component ────────────────────────────────────────────────────────────────

interface FoodPlotBoundaryLayerProps {
  locations: Waypoint[];
  visible: boolean;
}

/**
 * Renders food plot boundary polygons on the MapLibre map.
 * Must be rendered inside a HuntMap (MapProvider) context.
 * Handles style reloads and cleans up on unmount.
 */
export function FoodPlotBoundaryLayer({ locations, visible }: FoodPlotBoundaryLayerProps) {
  const map = useMap();
  const mountedRef = useRef(true);
  const visibleRef = useRef(visible);

  function removeLayers() {
    try {
      if (map.getLayer(FOOD_PLOT_LINE)) map.removeLayer(FOOD_PLOT_LINE);
      if (map.getLayer(FOOD_PLOT_FILL)) map.removeLayer(FOOD_PLOT_FILL);
      if (map.getSource(FOOD_PLOT_SOURCE)) map.removeSource(FOOD_PLOT_SOURCE);
    } catch { /* map may be destroyed or style not loaded */ }
  }

  function addLayers() {
    if (!mountedRef.current) return;
    const fc = buildFoodPlotFeatureCollection(locations);
    if (fc.features.length === 0) return;

    try {
      removeLayers();

      map.addSource(FOOD_PLOT_SOURCE, {
        type: 'geojson',
        data: fc,
      });

      map.addLayer({
        id: FOOD_PLOT_FILL,
        type: 'fill',
        source: FOOD_PLOT_SOURCE,
        paint: {
          'fill-color': '#22C55E',
          'fill-opacity': 0.2,
        },
      });

      map.addLayer({
        id: FOOD_PLOT_LINE,
        type: 'line',
        source: FOOD_PLOT_SOURCE,
        paint: {
          'line-color': '#22C55E',
          'line-width': 2,
        },
      });

      applyVisibility(visibleRef.current);
    } catch { /* map may be destroyed */ }
  }

  function applyVisibility(v: boolean) {
    const vStr = v ? 'visible' : 'none';
    try {
      if (map.getLayer(FOOD_PLOT_FILL)) map.setLayoutProperty(FOOD_PLOT_FILL, 'visibility', vStr);
      if (map.getLayer(FOOD_PLOT_LINE)) map.setLayoutProperty(FOOD_PLOT_LINE, 'visibility', vStr);
    } catch { /* map may be destroyed */ }
  }

  useEffect(() => {
    visibleRef.current = visible;
    applyVisibility(visible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  useEffect(() => {
    mountedRef.current = true;
    map.on('style.load', addLayers);
    if (map.isStyleLoaded()) addLayers();

    return () => {
      mountedRef.current = false;
      map.off('style.load', addLayers);
      removeLayers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locations]);

  return null;
}
